import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Form } from 'factorial-form'
import { NewWorkflowSurveyPageDocument } from 'generated/resources/graphql'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import AccessGroupForm from 'modules/surveys/components/AccessGroupForm'
import NewWorkflowSurveyModal from 'modules/surveys/components/New/NewWorkflowSurveyModal'
import { Types } from 'modules/surveys/lib/enums'

export default function NewWorkflowSurveyPage() {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading: loadingPolicies } = useQuery(NewWorkflowSurveyPageDocument)

  const canCreateForms = data?.surveys._policies.createForms.can ?? false

  const onClose = () => {
    track(trackingEvents.surveys.createSurveyCancel, { exit_step: 2 })
    navigate(navigation.processes.surveys.fullPath())
  }

  const [loading, setLoading] = React.useState(false)

  const accessGroupForm = useLazyRef(
    () =>
      new AccessGroupForm({
        strategy: 'members',
        groupable_id: null,
        groupable_type: null,
      })
  ).current

  const form = new Form(
    {
      name: null,
      category: null,
    },
    {
      name: 'string',
      category: 'string',
    }
  )

  const onSubmit = async (closePortal: ClosePortalFn) => {
    setLoading(true)

    const result = await asyncAction(() =>
      ApiClient().post('/surveys/v2/surveys/create_workflow_survey', {
        name: form.get('name').value,
        question_group_config: accessGroupForm.data(),
        workflow_category: form.get('category').value,
      })
    )

    setLoading(false)

    if (isSuccessful(result)) {
      const { value } = result
      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })

      track(trackingEvents.surveys.createSurveySubmit, { type: Types.workflow })
      navigate(navigation.processes.surveys.id.configuration.fullPath({ id: value.id }))
    }
  }

  if (!loadingPolicies && !canCreateForms) {
    return <Navigate to={navigation.processes.surveys.fullPath()}></Navigate>
  }

  return (
    <NewWorkflowSurveyModal loading={loading} form={form} onSubmit={onSubmit} onClose={onClose} />
  )
}
